/* @flow */
import React, { PureComponent } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Button } from 'common/components/core/button';
import { Loader } from 'common/components/core/loader/Loader';
import { fileDropzoneFactory } from 'common/components/core/dropzone/FileDropzone';
import PersonnelActions from 'common/redux/actions/personnel';
import {
    uploadInProgressSelector,
    uploadedImageSelector
} from 'common/redux/selectors/personnelSelectors';
import {
    personnelClientIdSelector,
    personnelIdSelector
} from 'common/redux/selectors/sessionSelector';
import { dashboardColors } from 'common/enums/ColorEnum';
import theme from './ProfilePhoto.scss';
import settingsTestIds from 'common/tests/mySettings/settings.json';

type ActionProps = {
    userPhotoExists: boolean
};

type CardProps = {
    t: Function
};

type Props = {
    imageUrl: string,
    isUploadInProgress: boolean,
    clientId: string,
    personnelId: string,
    t: Function
};

const ProfilePictureAction = function (props: ActionProps) {
    const { userPhotoExists, t, openFileDialog, onRemovePhoto } = props;

    if (userPhotoExists) {
        return (
            <div className={theme.changePictureActionContainer}>
                <div className={theme.changePictureAction}>
                    <Button
                        theme={theme}
                        className={theme.changePicture}
                        icon={'camera'}
                        label={t('changePicture')}
                        ripple={false}
                        semibold
                        onClick={openFileDialog}
                        testId={settingsTestIds.profilePhoto.change}
                    />
                    <div className={theme.note}>
                        {t('profilePhotoValidation')}
                    </div>
                </div>

                <Button
                    theme={theme}
                    className={theme.removeButton}
                    onClick={onRemovePhoto}
                    icon={'delete-bin'}
                    label={t('removePicture')}
                    ripple={false}
                    semibold
                    testId={settingsTestIds.profilePhoto.remove}
                />
            </div>
        );
    } else {
        return (
            <div className={theme.addPictureActionContainer}>
                <div className={theme.addPictureAction}>
                    <Button
                        theme={theme}
                        className={theme.addPicture}
                        icon={'plus-thin'}
                        label={t('addPicture')}
                        ripple={false}
                        semibold
                        onClick={openFileDialog}
                        testId={settingsTestIds.profilePhoto.add}
                    />
                    <div className={theme.note}>
                        {t('profilePhotoValidation')}
                    </div>
                </div>
            </div>
        );
    }
};

class ProfilePictureCard extends PureComponent<CardProps> {
    onRemovePhoto = () => {
        const { clientId, personnelId, removePhoto } = this.props;
        removePhoto(clientId, personnelId);
    };

    render() {
        const { t, isUploadInProgress, openFileDialog, imageUrl } = this.props;
        const iconStyle = imageUrl
            ? { backgroundImage: `url(${imageUrl})` }
            : {};
        return (
            <div className={theme.profilePhotoContainer}>
                <div className={theme.title}>{t('profilePicture')}</div>
                <div className={theme.body}>
                    <div className={theme.accountIcon} style={iconStyle}>
                        <Loader
                            backgroundColor={dashboardColors.paleGreySeven}
                            theme={theme}
                            className={theme.loader}
                            active={isUploadInProgress}
                        />
                    </div>
                    <div className={theme.actionContainer}>
                        <ProfilePictureAction
                            t={t}
                            disabled={isUploadInProgress}
                            onRemovePhoto={this.onRemovePhoto}
                            openFileDialog={openFileDialog}
                            userPhotoExists={!!imageUrl}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const ProfilePictureDropZone = fileDropzoneFactory({
    FileDropCard: translate('settings')(ProfilePictureCard)
});

class ProfilePhoto extends PureComponent<Props> {
    onDropHandler = ([file]) => {
        const { clientId, personnelId, uploadPhoto } = this.props;
        const formData = new FormData();
        formData.append('file', file);
        uploadPhoto(clientId, personnelId, formData);
    };

    render() {
        const {
            imageUrl,
            isUploadInProgress,
            clientId,
            personnelId,
            uploadPhoto,
            removePhoto,
            t
        } = this.props;
        return (
            <ProfilePictureDropZone
                t={t}
                accept={['image/png', 'image/jpg', 'image/jpeg']}
                maxSize={1000000}
                disableActiveStyling
                disableRejectStyling
                inlineDropzone
                onDrop={this.onDropHandler}
                isUploadInProgress={isUploadInProgress}
                imageUrl={imageUrl}
                clientId={clientId}
                personnelId={personnelId}
                uploadPhoto={uploadPhoto}
                removePhoto={removePhoto}
                className={theme.profilePhotoDropZone}
            />
        );
    }
}

const mapStateToProps = (state, props) => {
    const isUploadInProgress = uploadInProgressSelector(state, props);
    const imageUrl = uploadedImageSelector(state, props);
    const clientId = personnelClientIdSelector(state, props);
    const personnelId = personnelIdSelector(state, props);

    return {
        imageUrl,
        isUploadInProgress,
        clientId,
        personnelId
    };
};
const methodsToDispatch = {
    uploadPhoto: PersonnelActions.uploadPhoto,
    removePhoto: PersonnelActions.removePhoto
};

export default compose(
    connect(mapStateToProps, methodsToDispatch),
    translate('settings')
)(ProfilePhoto);
